import React, { useState, useRef, useEffect } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { cpp } from '@codemirror/lang-cpp';
import { java } from '@codemirror/lang-java';
import { oneDark } from '@codemirror/theme-one-dark';
import { javascript } from '@codemirror/lang-javascript';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { sql } from '@codemirror/lang-sql';
import axios from 'axios';
import { 
  Play, 
  Keyboard, 
  Terminal, 
  Trash2, 
  Moon, 
  Sun, 
  Wifi, 
  Clock, 
  ChevronDown,
  Code2,
  Sparkles
} from 'lucide-react';
import API_BASE_URL from '../Config/config';

const scrollbarStyles = `
  .compiler-vertical-ui *::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  .compiler-vertical-ui *::-webkit-scrollbar-track {
    background: transparent;
  }
  .compiler-vertical-ui *::-webkit-scrollbar-thumb {
    background: #1e293b;
    border-radius: 10px;
  }
  .compiler-vertical-ui *::-webkit-scrollbar-thumb:hover {
    background: #334155;
  }
  .cm-editor {
    height: 100% !important;
  }
  .cm-scroller {
    overflow: auto !important;
  }
`;

const SkillgenproCompiler = ({ userPlan = 'premium', chatBotRef, setShowChat }) => {
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('');
  const [output, setOutput] = useState('');
  const [outputError, setOutputError] = useState('');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [execTime, setExecTime] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const outputPanelRef = useRef(null);

  const languages = [
    { name: 'Python', ext: 'python', mode: python, plan: 'basic', icon: '🐍' },
    { name: 'C', ext: 'c', mode: cpp, plan: 'basic', icon: 'C' },
    { name: 'HTML', ext: 'html', mode: html, plan: 'basic', icon: '🌐' },
    { name: 'Java', ext: 'java', mode: java, plan: 'premium', icon: '☕' },
    { name: 'PostgreSQL', ext: 'postgresql', mode: sql, plan: 'premium', icon: '🐘' },
    { name: 'C++', ext: 'cpp', mode: cpp, plan: 'premium', icon: 'C++' },
    { name: 'React.js', ext: 'reactjs', mode: javascript, plan: 'premium', icon: '⚛️' },
    { name: 'CSS', ext: 'css', mode: css, plan: 'premium', icon: '🎨' },
    { name: 'TypeScript', ext: 'typescript', mode: () => javascript({ typescript: true }), plan: 'premium', icon: 'TS' },
    { name: 'Node.js', ext: 'nodejs', mode: javascript, plan: 'premium', icon: '🟢' }
  ];

  const templates = {
    python: 'print("Hello, World!")',
    c: '#include <stdio.h>\nint main() {\n  printf("Hello, World!\\n");\n  return 0;\n}',
    cpp: '#include <iostream>\nint main() {\n  std::cout << "Hello, World!";\n  return 0;\n}',
    java: 'public class Main {\n  public static void main(String[] args) {\n    System.out.println("Hello, World!");\n  }\n}',
    html: '<!DOCTYPE html>\n<html>\n<body style="background-color: #020617; color: #F8FAFC; font-family: sans-serif; display: flex; justify-content: center; align-items: center; height: 100vh;">\n  <h1>Hello, World!</h1>\n</body>\n</html>',
    css: 'body {\n  background-color: #020617;\n  color: #22D3EE;\n}',
    reactjs: 'function App() {\n  return <div style={{backgroundColor: "#020617", color: "#F8FAFC", padding: "20px", borderRadius: "10px", border: "1px solid #2563EB"}}>Hello, World!</div>;\n}',
    postgresql: 'SELECT * FROM skillgenpro;',
    typescript: 'const greet = (name: string): string => {\n  return `Hello, ${name}`;\n};\nconsole.log(greet("World"));',
    nodejs: 'console.log("Hello, World!");'
  };

  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState(''); // 'success', 'error', 'info'

  // Stats
  const [stats, setStats] = useState({ characters: 0, lines: 1, words: 0 });

  useEffect(() => {
    const lines = code.split('\n').length;
    const characters = code.length;
    const words = code.trim() ? code.trim().split(/\s+/).length : 0;
    setStats({ lines, characters, words });
  }, [code]);

  const handleRun = async () => {
    if (!language) return setShowWarning(true);
    setShowWarning(false);
    setLoading(true);
    setShowStatusPopup(false);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/execute`, { code, language, input });
      
      if (language === 'html' || language === 'css'|| language === 'reactjs') {
        setOutput(res.data.body || '');
      } else {
        setOutput(res.data.output || res.data.stdout || '');
      }   

      setOutputError(res.data.error || res.data.stderr || '');
      setExecTime(res.data.time || 'N/A');
      
      if (res.data.error || res.data.stderr) {
        setStatusMessage('Code execution failed');
        setStatusType('error');
      } else {
        setStatusMessage('Code compiled and executed successfully');
        setStatusType('success');
      }
      setShowStatusPopup(true);
      setTimeout(() => setShowStatusPopup(false), 3000);
      
      setTimeout(() => {
        outputPanelRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    } catch (err) {
      setOutput('');
      setOutputError(err.message || 'Error occurred');
      setExecTime('N/A');
      setStatusMessage('Code execution failed: ' + (err.message || 'Unknown error'));
      setStatusType('error');
      setShowStatusPopup(true);
      setTimeout(() => setShowStatusPopup(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!language || !code) return;
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `main.${language}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    setLanguage(lang);
    setCode(templates[lang] || '');
    setShowWarning(false);
  };

  const clearOutput = () => {
    setOutput('');
    setOutputError('');
  };

  const currentLangObj = languages.find(l => l.ext === language);

  return (
    <div className={`compiler-vertical-ui ${isDarkMode ? 'bg-[#0f172a]' : 'bg-bgMain'} min-h-screen w-full flex flex-col text-slate-300 font-body selection:bg-primary/30`}>
      <style>{scrollbarStyles}</style>

      {/* Modern Header */}
      <header className={`flex items-center justify-between px-6 py-4 border-b ${isDarkMode ? 'border-slate-800 bg-[#1e1b4b]' : 'border-accentGray bg-white shadow-soft'} z-10`}>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center text-white shadow-lg">
              <Code2 size={24} />
            </div>
            <h1 className={`text-xl font-black tracking-tight ${isDarkMode ? 'text-white' : 'text-heading'} font-display`}>The Skill Genie Compiler</h1>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <label className={`text-xs font-black uppercase tracking-widest ${isDarkMode ? 'text-slate-400' : 'text-body'}`}>Language</label>
            <div className="relative group">
              <select
                className={`appearance-none bg-transparent ${isDarkMode ? 'text-white border-slate-700 hover:border-slate-500' : 'text-heading border-accentGray hover:border-primary'} border rounded-button px-4 py-2 pr-10 font-bold focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all cursor-pointer`}
                value={language}
                onChange={handleLanguageChange}
              >
                <option value="" disabled>Select Language</option>
                {languages.map((lang) => (
                  <option key={lang.ext} value={lang.ext} className={isDarkMode ? 'bg-[#1e293b]' : 'bg-white'}>
                    {lang.name} {lang.plan === 'premium' ? '★' : ''}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-50" />
            </div>
          </div>

          <div className="flex items-center gap-2 border-l border-white/10 pl-6">
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-2.5 rounded-xl transition-all ${isDarkMode ? 'hover:bg-white/10 text-slate-300' : 'hover:bg-bgMain text-body'}`}
            >
              {isDarkMode ? <Sun size={22} /> : <Moon size={22} />}
            </button>

            <button
              onClick={handleRun}
              disabled={loading || !language}
              className={`flex items-center gap-2 px-8 py-2.5 btn-primary-gradient disabled:opacity-50 disabled:cursor-not-allowed ml-2`}
            >
              {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Play size={20} fill="currentColor" />}
              <span className="text-lg font-bold">{loading ? 'Running...' : 'Run'}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Area */}
      <main className="flex-1 flex overflow-hidden">
        {/* Editor Side */}
        <div className="flex-1 flex flex-col border-r border-slate-800 relative">
          {/* Editor Tabs */}
          <div className={`flex items-center px-4 pt-4 ${isDarkMode ? 'bg-[#0f172a]' : 'bg-bgMain'}`}>
            <div className={`flex items-center gap-2 px-6 py-2.5 rounded-t-card font-bold text-sm border-t border-x ${isDarkMode ? 'bg-[#1e293b] border-slate-800 text-white shadow-soft' : 'bg-white border-accentGray text-heading shadow-soft'}`}>
              <span className="text-base">{currentLangObj?.icon || '📄'}</span>
              <span className="uppercase tracking-widest">{language ? `MAIN.${language.toUpperCase()}` : 'MAIN.PY'}</span>
            </div>
          </div>

          <div className="flex-1 overflow-hidden relative group">
            {showWarning && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-warning/10 border border-warning/50 text-warning px-6 py-2.5 rounded-button text-sm font-bold backdrop-blur-md animate-bounce">
                ⚠ Please select a language before coding.
              </div>
            )}
            <CodeMirror
              value={code}
              height="100%"
              extensions={language ? [languages.find((l) => l.ext === language)?.mode?.()] : []}
              theme={oneDark}
              onChange={(val) => setCode(val)}
              className="h-full text-base font-mono"
            />
          </div>
        </div>

        {/* IO Side */}
        <div className={`w-[500px] flex flex-col gap-6 p-6 ${isDarkMode ? 'bg-[#1e1b4b]/50' : 'bg-white'} overflow-y-auto`}>
          {/* Input Panel */}
          <section className={`flex flex-col rounded-card border ${isDarkMode ? 'bg-[#1e293b] border-slate-800' : 'bg-bgMain border-accentGray'} overflow-hidden transition-all shadow-soft`}>
            <div className={`flex items-center gap-3 px-6 py-4 border-b ${isDarkMode ? 'border-slate-800' : 'border-accentGray'}`}>
              <div className="p-2 bg-primary/10 rounded-xl text-primary">
                <Keyboard size={20} />
              </div>
              <h3 className={`text-sm font-black uppercase tracking-widest ${isDarkMode ? 'text-slate-300' : 'text-heading'}`}>STDIN (INPUT)</h3>
            </div>
            <div className="p-6">
              <textarea 
                value={input} 
                onChange={e => setInput(e.target.value)}
                placeholder="Paste inputs here..."
                className={`w-full min-h-[140px] bg-transparent resize-none outline-none font-mono text-sm leading-relaxed ${isDarkMode ? 'text-slate-300 placeholder-slate-600' : 'text-slate-800 placeholder-slate-400'}`}
                spellCheck={false}
              />
            </div>
          </section>

          {/* Output Panel */}
          <section className={`flex-1 flex flex-col rounded-2xl border ${isDarkMode ? 'bg-[#1e293b] border-slate-800' : 'bg-white border-gray-200 shadow-sm'} overflow-hidden transition-all hover:border-slate-700`}>
            <div className={`flex items-center justify-between px-5 py-3 border-b ${isDarkMode ? 'border-slate-800' : 'border-gray-100'}`}>
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-slate-700/50 rounded-lg text-slate-400">
                  <Terminal size={18} />
                </div>
                <h3 className={`text-sm font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>STDOUT (OUTPUT)</h3>
              </div>
              <button 
                onClick={clearOutput}
                className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-red-400 transition-colors uppercase tracking-widest"
              >
                <Trash2 size={14} />
                Clear
              </button>
            </div>
            
            <div className="flex-1 p-5 overflow-y-auto font-mono text-sm leading-relaxed relative" ref={outputPanelRef}>
              {!output && !outputError ? (
                <div className={`h-full flex flex-col items-center justify-center gap-2 ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`}>
                  <Terminal size={32} strokeWidth={1} />
                  <p>Output will appear here...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {outputError && (
                    <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl whitespace-pre-wrap animate-in fade-in slide-in-from-top-2">
                      <div className="flex items-center gap-2 mb-2 font-bold text-xs uppercase tracking-widest">
                        <span>🚨 Execution Error</span>
                      </div>
                      {outputError}
                    </div>
                  )}
                  {output && (
                    <div className={`whitespace-pre-wrap ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'} animate-in fade-in`}>
                      {output}
                    </div>
                  )}
                </div>
              )}

              {/* AI Feedback Button Overlay */}
              {(output || outputError) && (
                <div className="sticky bottom-0 left-0 right-0 pt-8 pb-2 flex justify-center bg-gradient-to-t from-[#1e293b] via-[#1e293b]/80 to-transparent">
                  <button
                    onClick={() => {
                      if (setShowChat) {
                        setShowChat(true);
                        setTimeout(() => {
                          if (chatBotRef && chatBotRef.current) {
                            const visibleMessage = "Get AI feedback for my code.";
                            const detailedPrompt = `Act as an expert coding assistant.\nAnalyze this code, its input, output, errors, and execution time.\nSpot bugs, suggest improvements, and highlight best practices.\n\n--- Code ---\n${code}\n\n--- Input ---\n${input}\n\n--- Output ---\n${output}\n\n--- Error ---\n${outputError}\n\n--- Execution Time ---\n${execTime}\n`;
                            chatBotRef.current.receiveAnalysis(detailedPrompt, visibleMessage);
                          }
                        }, 250);
                      }
                    }}
                    className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold shadow-xl shadow-purple-500/20 hover:scale-105 active:scale-95 transition-all text-sm group"
                  >
                    <Sparkles size={18} className="group-hover:animate-pulse" />
                    Get AI Feedback
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>

      {/* Footer / Status Bar */}
      <footer className={`flex items-center justify-between px-6 py-2 border-t text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'border-slate-800 bg-[#0b0f1a] text-slate-500' : 'border-gray-200 bg-white text-slate-400'}`}>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${loading ? 'bg-amber-400 animate-pulse' : 'bg-emerald-500'}`} />
            <span>{loading ? 'Executing' : 'Connected'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Wifi size={12} />
            <span>Latency: <span className={isDarkMode ? 'text-purple-400' : 'text-purple-600'}>42ms</span></span>
          </div>
          <div className="flex items-center gap-2 border-l border-slate-800 pl-6 ml-2">
            <Clock size={12} />
            <span>Time: <span className={isDarkMode ? 'text-blue-400' : 'text-blue-600'}>{execTime}</span></span>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <span>Characters: <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{stats.characters}</span></span>
          <span>Lines: <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{stats.lines}</span></span>
          <span>Words: <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{stats.words}</span></span>
        </div>
      </footer>

      {/* Status Popup */}
      {showStatusPopup && (
        <div className="fixed top-20 right-8 z-50 animate-in fade-in slide-in-from-right-4">
          <div className={`px-6 py-3 rounded-xl shadow-2xl backdrop-blur-md flex items-center gap-3 border ${
            statusType === 'success' ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-500' : 
            statusType === 'error' ? 'bg-red-500/10 border-red-500/50 text-red-400' : 
            'bg-blue-500/10 border-blue-500/50 text-blue-400'
          } font-bold text-sm`}>
            <div className={`w-2 h-2 rounded-full ${statusType === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`} />
            {statusMessage}
          </div>
        </div>
      )}
    </div>
  );
}

export default SkillgenproCompiler;



